import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, fmtDate, errMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDeliveryStatus } from "@/components/delivery/useDeliveryStatus";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, PackageCheck, ChevronRight, Truck, PowerOff, RefreshCw, PackageX } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  assigned: "Ready for pickup",
  out_for_delivery: "In transit",
  delivered: "Delivered",
  cancelled: "Cancelled",
  returned: "Returned",
};

export default function Runs() {
  const { user } = useAuth();
  const { online, toggle } = useDeliveryStatus();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders/assigned-to-me?limit=50");
      setRows((data.items || []).filter((o) => !["delivered", "cancelled", "returned", "refunded"].includes(o.status)));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const active = rows.filter((o) => ["picked_up", "in_transit"].includes(o.delivery?.status) || o.status === "out_for_delivery");
  const readyForPickup = rows.filter((o) => o.status === "assigned" && !["picked_up", "in_transit"].includes(o.delivery?.status));

  return (
    <div className="p-4 space-y-4" data-testid="delivery-runs-page">
      {/* Status card */}
      <Card className="card-soft border-transparent" data-testid="online-status-card">
        <CardContent className="p-4 flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl grid place-items-center ${online ? "bg-accent/15 text-accent" : "bg-secondary text-muted-foreground"}`}>
            {online ? <Truck className="w-6 h-6" /> : <PowerOff className="w-6 h-6" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-lg font-display font-semibold leading-tight">
              {online ? "You're online" : "You're offline"}
            </div>
            <div className="text-xs text-muted-foreground">
              {online ? "New orders can be assigned to you" : "Toggle on to accept assignments"}
            </div>
          </div>
          <Switch
            checked={online}
            onCheckedChange={(v) => toggle(v)}
            data-testid="online-toggle"
            className="data-[state=checked]:bg-accent"
          />
        </CardContent>
      </Card>

      {/* Active run */}
      {active.length > 0 && (
        <section data-testid="active-runs-section">
          <div className="flex items-center justify-between mb-2 mt-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Active run</div>
            <Badge className="bg-primary hover:bg-primary text-primary-foreground animate-pulse">Live</Badge>
          </div>
          {active.map((o) => <OrderRow key={o.id} o={o} highlight />)}
        </section>
      )}

      {/* Ready for pickup */}
      <section data-testid="ready-runs-section">
        <div className="flex items-center justify-between mb-2 mt-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            {online ? `Ready to pick up (${readyForPickup.length})` : "Offline"}
          </div>
          <Button variant="ghost" size="sm" onClick={load} className="h-7 px-2 text-xs" data-testid="refresh-runs">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
        </div>

        {loading && Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full mb-2" />
        ))}

        {!loading && !online && (
          <Card className="card-soft border-transparent" data-testid="offline-empty">
            <CardContent className="p-8 text-center">
              <PowerOff className="w-10 h-10 mx-auto text-muted-foreground opacity-40 mb-2" />
              <div className="font-medium">You're offline</div>
              <div className="text-sm text-muted-foreground">Turn on to start accepting orders.</div>
            </CardContent>
          </Card>
        )}

        {!loading && online && readyForPickup.length === 0 && active.length === 0 && (
          <Card className="card-soft border-transparent" data-testid="no-runs">
            <CardContent className="p-8 text-center">
              <PackageX className="w-10 h-10 mx-auto text-muted-foreground opacity-40 mb-2" />
              <div className="font-medium">No assigned runs right now</div>
              <div className="text-sm text-muted-foreground">Hang tight — the manager will assign you an order soon.</div>
            </CardContent>
          </Card>
        )}

        {!loading && online && readyForPickup.map((o) => <OrderRow key={o.id} o={o} />)}
      </section>

      {/* Greeting footer */}
      <div className="text-center text-xs text-muted-foreground pt-4">
        Signed in as <span className="font-medium text-foreground">{user?.name || user?.email}</span>
      </div>
    </div>
  );
}

function OrderRow({ o, highlight = false }) {
  const items = (o.items || []).reduce((s, x) => s + x.qty, 0);
  const partnerPicked = ["picked_up", "in_transit"].includes(o.delivery?.status);
  return (
    <Link to={`/delivery/order/${o.id}`} data-testid={`run-row-${o.order_number}`}>
      <Card className={`card-soft border-transparent mb-2 hover:shadow-md transition ${highlight ? "border-l-4 border-l-primary" : ""}`}>
        <CardContent className="p-4 flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${partnerPicked ? "bg-primary/10 text-primary" : "bg-accent/15 text-accent"}`}>
            {partnerPicked ? <Truck className="w-5 h-5" /> : <PackageCheck className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-mono font-semibold text-sm">{o.order_number}</div>
              <Badge variant="outline" className="text-[10px] capitalize">{STATUS_LABEL[o.status] || o.status?.replace(/_/g, " ")}</Badge>
            </div>
            <div className="text-sm mt-1 truncate">
              <MapPin className="w-3 h-3 inline text-muted-foreground mr-1" />
              {o.address?.city} · {o.address?.pincode}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {items} item{items > 1 ? "s" : ""} · {money(o.pricing?.total)} · {(o.payment?.method || "").toUpperCase()}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">Placed {fmtDate(o.created_at)}</div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground mt-1 shrink-0" />
        </CardContent>
      </Card>
    </Link>
  );
}
